import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface NotificationBannerProps {
  title: string;
  message: string;
  photo?: string | null;
  action?: () => void;
  duration?: number;
  onDismiss: () => void;
}

export function NotificationBanner({
  title,
  message,
  photo,
  action,
  duration = 4000,
  onDismiss,
}: NotificationBannerProps) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, duration);
    return () => clearTimeout(timer);
  }, [duration, onDismiss]);

  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      transition={{ type: 'spring', damping: 20 }}
      onClick={action}
      style={{
        position: 'fixed',
        top: 16,
        left: 16,
        right: 16,
        zIndex: 9999,
        background: 'linear-gradient(135deg, #1a0000 0%, #0d0d0d 100%)',
        border: '1px solid #e11d48',
        borderRadius: 16,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: action ? 'pointer' : 'default',
        boxShadow: '0 8px 32px rgba(225,29,72,0.3)',
      }}
    >
      {photo && (
        <img
          src={photo}
          alt=""
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            objectFit: 'cover',
            border: '2px solid #e11d48',
            flexShrink: 0,
          }}
        />
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            color: '#ffffff',
            fontWeight: 700,
            fontSize: 14,
            margin: '0 0 2px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </p>
        <p
          style={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: 12,
            margin: 0,
          }}
        >
          {message}
        </p>
      </div>

      <button
        onClick={e => {
          e.stopPropagation();
          onDismiss();
        }}
        style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.4)',
          cursor: 'pointer',
          padding: 4,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <X size={18} />
      </button>
    </motion.div>
  );
}
